/**
 * Module dependencies.
 */

import { SubTitle } from 'app/components/atoms/sub-title/sub-title';
import { FormListField } from 'app/components/organisms/crud/form-types';
import { numericRule, requiredRule } from 'app/core/utils/field-rules';
import { usePaginatedCrudRequest } from 'app/hooks/app-requests/crud/use-paginated-crud-request';
import { Drug } from 'app/types/drug';
import { TFunction } from 'i18next';
import { compact, get } from 'lodash';
import React from 'react';
import styles from '../sales-list.module.less';
import { PosologiesForm } from './posologies-form';
import { translateField } from 'app/core/utils/text';

/**
 * Export `saleItemsFormField` function.
 */

export const saleItemsFormField = (translate: TFunction, prefix = ''): FormListField[] => [
  {
    type: 'columns',
    name: 'mainWire',
    columns: [
      {
        column: 6,
        children: [
          {
            type: 'inputField',
            name: compact([prefix, 'externalItemId']).join('.'),
            label: translate('users.sales.add.labels.externalItemId'),
            rules: requiredRule(translate)
          }
        ]
      },
      {
        column: 6,
        children: [
          {
            type: 'inputField',
            name: compact([prefix, 'vatTax']).join('.'),
            label: translate('users.sales.add.labels.vatTax'),
            rules: undefined
          }
        ]
      },
      {
        column: 6,
        children: [
          {
            type: 'inputField',
            name: compact([prefix, 'quantity']).join('.'),
            label: translate('users.sales.add.labels.quantity'),
            rules: numericRule(translate)
          }
        ]
      },
      {
        column: 6,
        children: [
          {
            type: 'inputField',
            name: compact([prefix, 'price']).join('.'),
            label: translate('users.sales.add.labels.price'),
            rules: undefined
          }
        ]
      }
    ]
  },
  {
    type: 'columns',
    name: 'mainWire',
    columns: [
      {
        column: 8,
        children: [
          {
            type: 'selectField',
            name: compact([prefix, 'productId']).join('.'),
            showSearch: true,
            allowClear: true,
            rules: undefined,
            onChange: (value, option, form) => {
              form.resetField(compact([prefix, 'productCode']).join('.'));
              form.resetField(compact([prefix, 'productName']).join('.'));
              form.setValue(compact([prefix, 'productCode']).join('.'), (option as any)?.registry);
              form.setValue(compact([prefix, 'productName']).join('.'), (option as any)?.label);
            },
            label: translate('users.sales.add.labels.product'),
            paginated: {
              enabled: true,
              pageSize: 50
            },
            options: {
              hook: usePaginatedCrudRequest as any,
              hookProps: (_values: any, _params: any, options: any) => {
                const searchValue = options?.searchValue?.trim();

                return [
                  {
                    key: ['drugs-paginated-sales', searchValue || ''],
                    endpoint: 'drugs' as any,
                    options: {
                      ...(searchValue ? { search: searchValue } : {})
                    },
                    pageSize: 50
                  }
                ];
              },
              normalize: (items: Drug[]) => {
                if (!items || !Array.isArray(items)) {
                  return [];
                }

                return items
                  .filter(item => item && item.id)
                  .map(item => ({
                    value: item.id,
                    registry: item.registry,
                    label: translateField(item.name, 'pt'),
                    searchValue: translateField(item.name, 'pt')
                  }));
              }
            }
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'inputField',
            disabled: values => {
              return !!get(values, compact([prefix, 'productId']).join('.'));
            },
            name: compact([prefix, 'productCode']).join('.'),
            rules: requiredRule(translate),
            label: translate('users.sales.add.labels.productCode')
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'inputField',
            disabled: values => {
              return !!get(values, compact([prefix, 'productCode']).join('.'));
            },
            name: compact([prefix, 'productName']).join('.'),
            rules: requiredRule(translate),
            label: translate('users.sales.add.labels.productName')
          }
        ]
      }
    ]
  },
  {
    type: 'columns',
    name: 'mainWire',
    columns: [
      {
        column: 8,
        children: [
          {
            type: 'inputField',
            name: compact([prefix, 'prescriptionNumber']).join('.'),
            label: translate('users.sales.add.labels.prescriptionNumber'),
            rules: undefined
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'checkBoxField',
            name: compact([prefix, 'renewal']).join('.'),
            label: translate('users.sales.add.labels.renewal'),
            rules: undefined
          }
        ]
      },
      {
        column: 8,
        children: [
          {
            type: 'selectField',
            name: compact([prefix, 'status']).join('.'),
            label: translate('users.sales.add.labels.status'),
            rules: requiredRule(translate),
            options: [
              {
                value: 'pending',
                label: String(translate('users.sales.add.labels.statusLabels.pending'))
              },
              {
                value: 'ready',
                label: String(translate('users.sales.add.labels.statusLabels.ready'))
              },
              {
                value: 'completed',
                label: String(translate('users.sales.add.labels.statusLabels.completed'))
              }
            ]
          }
        ]
      }
    ]
  },
  {
    type: 'inputField',
    inputType: 'textArea',
    name: compact([prefix, 'instructions']).join('.'),
    label: translate('users.sales.add.labels.instructions'),
    rules: undefined
  },
  {
    type: 'render',
    name: 'render_2',
    render: (
      <div className={styles.orderItemHeader}>
        <SubTitle>{translate('users.sales.add.posologyItems')}</SubTitle>
      </div>
    )
  },
  {
    type: 'wrapper',
    name: 'wrapper',
    metadata: { prefix },
    wrapperComponent: PosologiesForm,
    formFields: [
      /* inside component */
    ]
  }
];
